"use client";

import type { Route } from "next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Award, BookOpen, CheckCircle, XCircle } from "lucide-react";

import { loadQuizData, type QuizId } from "./registry";
import type { McqQuestion, OpenEndedQuestion, QuizData } from "./types";
import { renderInlineMath } from "./render-math";
import { getQuizOptionOrder } from "./option-order";
import BusQuizCta from "./BusQuizCta";
import ShareQuizResult from "./ShareQuizResult";
import StepReveal from "./StepReveal";
import QuizWhatsAppCta from "./QuizWhatsAppCta";
import { TelegramCTA } from "@/app/components/TelegramCTA";
import {
  createQuizAttemptId,
  trackQuizCtaView,
  trackQuizEvent,
  type QuizLoadFailureReason,
  type QuizStudyContext,
} from "@/lib/quiz-analytics";
import { quizIdToUrl } from "@/lib/quizzes/quiz-slugs";
import {
  BUS_QUIZ_STORAGE_VERSION,
  quizStorageKeys,
  quizProgressLocaleForHref,
} from "./bus-quiz-storage";

import type { Locale } from "@/lib/i18n/locales";
import { quizEmbeddedCopy } from "@/lib/i18n/quiz-embedded-copy";
import { formatQuizRunner } from "@/lib/i18n/quiz-runner-copy";

type McqAnswerMap = Record<number, number | undefined>;

type ActiveSection = "mcq" | "open";

export type QuizProps = {
  quizId: QuizId;
  title?: string;
  subtitle?: string;
  initialQuizData?: QuizData;
  /** Explicit accepted counterpart for private/native quiz journeys. */
  busQuizHref?: Route;
  studyContext?: QuizStudyContext | null;
  locale?: Locale;
};

export default function Quiz(props: QuizProps) {
  // Remount before effects can save the previous session into a new language/version.
  const identity = `${props.locale ?? "en"}:${props.quizId}:${props.initialQuizData?.meta.persistenceRevision ?? props.initialQuizData?.meta.revision ?? ""}`;
  return <QuizSession key={identity} {...props} />;
}

function QuizSession({
  quizId,
  title,
  subtitle,
  initialQuizData,
  busQuizHref,
  studyContext = null,
  locale = "en",
}: QuizProps) {
  const copy = useMemo(() => quizEmbeddedCopy(locale), [locale]);
  const [quizData, setQuizData] = useState<QuizData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [canRetry, setCanRetry] = useState(false);
  const trackedLoadFailuresRef = useRef(new Set<string>());
  const studySubject = studyContext?.studySubject;
  const studyTopic = studyContext?.studyTopic;

  const reportLoadFailure = useCallback(
    (failureReason: QuizLoadFailureReason) => {
      const failureKey = `${quizId}:${failureReason}`;
      if (trackedLoadFailuresRef.current.has(failureKey)) return;
      trackedLoadFailuresRef.current.add(failureKey);
      trackQuizEvent("quiz_load_failed", {
        quizId,
        surface: "embedded",
        mode: "full",
        failure_reason: failureReason,
        ...(studySubject && { studySubject }),
        ...(studyTopic && { studyTopic }),
      });
    },
    [quizId, studySubject, studyTopic]
  );

  useEffect(() => {
    setCanRetry(false);
    if (initialQuizData) {
      const hasQuestions =
        (initialQuizData.mcqQuestions?.length ?? 0) > 0 ||
        (initialQuizData.openEndedQuestions?.length ?? 0) > 0;
      if (!hasQuestions) {
        setQuizData(null);
        setLoadError(copy.empty);
        setIsLoading(false);
        reportLoadFailure("empty_question_bank");
        return;
      }
      setQuizData(initialQuizData);
      setLoadError(null);
      setIsLoading(false);
      return;
    }
    if (locale !== "en") {
      setQuizData(null);
      setLoadError(copy.unavailable);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    setQuizData(null);

    loadQuizData(quizId)
      .then((data) => {
        if (cancelled) return;
        const hasQuestions =
          (data?.mcqQuestions?.length ?? 0) > 0 ||
          (data?.openEndedQuestions?.length ?? 0) > 0;
        if (data && hasQuestions) {
          setQuizData(data);
        } else if (data) {
          setLoadError(copy.empty);
          reportLoadFailure("empty_question_bank");
        } else {
          setLoadError(formatQuizRunner(copy.notFound, { quizId }));
          reportLoadFailure("not_found");
        }
      })
      .catch((error) => {
        if (cancelled) return;
        if (process.env.NODE_ENV !== "production") {
          console.error(`Failed to load quiz data for ${quizId}:`, error);
        }
        setLoadError(copy.loadError);
        setCanRetry(true);
        reportLoadFailure("load_error");
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [initialQuizData, quizId, reportLoadFailure, loadAttempt, locale, copy]);

  if (isLoading) {
    return (
      <div className="not-prose">
        <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 shadow-sm">
          <div className="animate-pulse rounded-xl bg-white p-6 shadow">
            <div className="mb-4 h-6 w-1/2 rounded bg-gray-200" />
            <div className="space-y-2">
              <div className="h-4 w-full rounded bg-gray-200" />
              <div className="h-4 w-5/6 rounded bg-gray-200" />
              <div className="h-4 w-2/3 rounded bg-gray-200" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!quizData || loadError) {
    return (
      <div className="not-prose">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
          {loadError ?? formatQuizRunner(copy.notFound, { quizId })}
          {canRetry && (
            <button
              type="button"
              className="mt-3 block min-h-11 px-3 font-semibold underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              onClick={() => setLoadAttempt((attempt) => attempt + 1)}
            >
              {copy.retry}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <QuizInner
      key={`${locale}:${quizId}:${quizData.meta.persistenceRevision ?? quizData.meta.revision ?? ""}`}
      quizId={quizId}
      quizData={quizData}
      title={title}
      subtitle={subtitle}
      studyContext={studyContext}
      busQuizHref={busQuizHref}
      locale={locale}
    />
  );
}

type QuizInnerProps = QuizProps & {
  quizData: QuizData;
};

function QuizInner({
  busQuizHref,
  quizId,
  quizData,
  title,
  subtitle,
  studyContext,
  locale = "en",
}: QuizInnerProps) {
  const copy = useMemo(() => quizEmbeddedCopy(locale), [locale]);
  const mcqQuestions: McqQuestion[] = quizData.mcqQuestions ?? [];
  const openEndedQuestions: OpenEndedQuestion[] =
    quizData.openEndedQuestions ?? [];

  const hasMcq = mcqQuestions.length > 0;
  const hasOpen = openEndedQuestions.length > 0;

  const initialSection: ActiveSection = hasMcq
    ? "mcq"
    : hasOpen
    ? "open"
    : "mcq";

  const [currentSection, setCurrentSection] =
    useState<ActiveSection>(initialSection);
  const [mcqAnswers, setMcqAnswers] = useState<McqAnswerMap>({});
  const [showMcqResults, setShowMcqResults] = useState(false);
  const visibilitySentinelRef = useRef<HTMLSpanElement>(null);
  const visibleRef = useRef(false);

  // Refs for abandonment tracking - readable in effect cleanups without stale closures
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const abandonedRef = useRef(false);
  const attemptIdRef = useRef(createQuizAttemptId());
  const attemptStartedAtRef = useRef<number | null>(null);
  const answeredQuestionIdsRef = useRef(new Set<number>());
  const mcqAnswersRef = useRef(mcqAnswers);
  mcqAnswersRef.current = mcqAnswers;
  // Fire quiz_started at most once, and only after a deliberate interaction.
  // Visibility is an impression, not evidence that a student attempted work.
  const markStarted = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    attemptStartedAtRef.current = Date.now();
    trackQuizEvent("quiz_started", {
      quizId,
      attemptId: attemptIdRef.current,
      surface: "embedded",
      mode: "full",
      total: mcqQuestions.length,
      ...studyContext,
    });
  }, [quizId, mcqQuestions.length, studyContext]);

  useEffect(() => {
    setMcqAnswers({});
    setShowMcqResults(false);
    setCurrentSection(initialSection);
    // Reset abandonment flags when quiz ID changes
    startedRef.current = false;
    completedRef.current = false;
    abandonedRef.current = false;
    attemptIdRef.current = createQuizAttemptId();
    attemptStartedAtRef.current = null;
    answeredQuestionIdsRef.current.clear();
    visibleRef.current = false;
    trackQuizEvent("quiz_viewed", {
      quizId,
      attemptId: attemptIdRef.current,
      surface: "embedded",
      mode: "full",
      total: mcqQuestions.length,
      ...studyContext,
    });
  }, [quizId, initialSection, mcqQuestions.length, studyContext]);

  useEffect(() => {
    const sentinel = visibilitySentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === "undefined") return;

    let visibilityTimer: number | null = null;
    const clearVisibilityTimer = () => {
      if (visibilityTimer === null) return;
      window.clearTimeout(visibilityTimer);
      visibilityTimer = null;
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (visibleRef.current) return;
        if (!entry?.isIntersecting || entry.intersectionRatio < 0.5) {
          clearVisibilityTimer();
          return;
        }
        if (visibilityTimer !== null) return;
        visibilityTimer = window.setTimeout(() => {
          visibleRef.current = true;
          observer.disconnect();
          trackQuizEvent("quiz_visible", {
            quizId,
            attemptId: attemptIdRef.current,
            surface: "embedded",
            mode: "full",
            total: mcqQuestions.length,
            elapsed_seconds: 1,
            ...studyContext,
          });
        }, 1000);
      },
      { threshold: 0.5 }
    );

    observer.observe(sentinel);
    return () => {
      clearVisibilityTimer();
      observer.disconnect();
    };
  }, [quizId, mcqQuestions.length, studyContext]);

  // Analytics: quiz_abandoned on unmount or tab hide after an actual attempt.
  useEffect(() => {
    if (mcqQuestions.length === 0) return;

    const fireAbandoned = () => {
      if (!startedRef.current || completedRef.current || abandonedRef.current)
        return;
      abandonedRef.current = true;
      trackQuizEvent("quiz_abandoned", {
        quizId,
        attemptId: attemptIdRef.current,
        surface: "embedded",
        mode: "full",
        questions_answered: Object.keys(mcqAnswersRef.current).length,
        total: mcqQuestions.length,
        elapsed_seconds: attemptStartedAtRef.current
          ? Math.round((Date.now() - attemptStartedAtRef.current) / 1000)
          : undefined,
        ...studyContext,
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        fireAbandoned();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      fireAbandoned();
    };
  }, [quizId, mcqQuestions.length, studyContext]);

  const handleMcqChange = (questionId: number, optionIndex: number) => {
    // Answering counts as reaching the quiz even if the observer hasn't fired.
    markStarted();
    if (!answeredQuestionIdsRef.current.has(questionId)) {
      answeredQuestionIdsRef.current.add(questionId);
      trackQuizEvent("quiz_answered", {
        quizId,
        attemptId: attemptIdRef.current,
        surface: "embedded",
        mode: "full",
        total: mcqQuestions.length,
        questions_answered: answeredQuestionIdsRef.current.size,
        question_number:
          mcqQuestions.findIndex((question) => question.id === questionId) + 1,
        answer_correct:
          mcqQuestions.find((question) => question.id === questionId)
            ?.correct === optionIndex,
        elapsed_seconds: attemptStartedAtRef.current
          ? Math.round((Date.now() - attemptStartedAtRef.current) / 1000)
          : 0,
        ...studyContext,
      });
    }
    setMcqAnswers((prev) => ({ ...prev, [questionId]: optionIndex }));
  };

  const checkMcqAnswers = () => {
    completedRef.current = true;
    setShowMcqResults(true);
    const total = mcqQuestions.length;
    const score = mcqQuestions.reduce(
      (c, q) => (q.correct === mcqAnswers[q.id] ? c + 1 : c),
      0
    );
    const wrongIds = mcqQuestions
      .filter((q) => q.correct !== mcqAnswers[q.id])
      .map((q) => q.id);
    trackQuizEvent("quiz_completed", {
      quizId,
      attemptId: attemptIdRef.current,
      surface: "embedded",
      mode: "full",
      score,
      total,
      questions_answered: Object.keys(mcqAnswers).length,
      elapsed_seconds: attemptStartedAtRef.current
        ? Math.round((Date.now() - attemptStartedAtRef.current) / 1000)
        : undefined,
      wrongIds,
      ...studyContext,
    });

    // Impression for the CTA the results screen is about to render. Mirrors the
    // render condition below (score < 80% -> WhatsApp, else Telegram) so the
    // click events have a denominator.
    trackQuizCtaView({
      quizId,
      cta: total > 0 && score / total < 0.8 ? "whatsapp" : "telegram",
      score,
      total,
      studyContext,
    });

    // Persist to localStorage so the parent progress report can read it
    try {
      const {progress:progressKey,completion:completionKey,score:scoreKey,mistakes:mistakesKey} = quizStorageKeys(quizId, locale, quizData.meta.persistenceRevision);

      window.localStorage.setItem(
        progressKey,
        JSON.stringify({
          version: BUS_QUIZ_STORAGE_VERSION,
          ...(quizData.meta.revision && { revision: quizData.meta.revision }),
          ...(quizData.meta.persistenceRevision && { persistenceRevision: quizData.meta.persistenceRevision }),
          currentIndex: total - 1,
          totalQuestions: total,
          answers: mcqAnswers,
          checkedIds: mcqQuestions.map((q) => q.id),
        })
      );
      window.localStorage.setItem(completionKey, new Date().toISOString());
      window.localStorage.setItem(scoreKey, JSON.stringify({ score, total }));
      if (wrongIds.length > 0) {
        window.localStorage.setItem(mistakesKey, JSON.stringify(wrongIds));
      } else {
        window.localStorage.removeItem(mistakesKey);
      }
    } catch {
      // localStorage may be unavailable - fail silently
    }
  };

  const resetMcq = () => {
    setMcqAnswers({});
    setShowMcqResults(false);
  };

  const calculateMcqScore = () =>
    mcqQuestions.reduce((correct, question) => {
      return question.correct === mcqAnswers[question.id]
        ? correct + 1
        : correct;
    }, 0);

  const displayTitle = title ?? quizData.meta.title;
  const displaySubtitle = subtitle ?? quizData.meta.subtitle;

  const sections = useMemo(
    () =>
      [
        hasMcq && {
          key: "mcq" as const,
          label: formatQuizRunner(copy.mcq, { count: mcqQuestions.length }),
        },
        hasOpen && {
          key: "open" as const,
          label: formatQuizRunner(copy.open, {
            count: openEndedQuestions.length,
          }),
        },
      ].filter(Boolean) as Array<{ key: ActiveSection; label: string }>,
    [hasMcq, hasOpen, mcqQuestions.length, openEndedQuestions.length, copy]
  );

  return (
    <div className="not-prose">
      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 shadow-sm">
        <div className="rounded-xl bg-white p-6 shadow">
          <div className="mb-4 flex items-center gap-3">
            <BookOpen className="text-indigo-600" size={32} />
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                {displayTitle}
              </h2>
              {displaySubtitle ? (
                <p className="text-sm text-gray-600">
                  {renderInlineMath(displaySubtitle, `quiz-subtitle-${quizId}`)}
                </p>
              ) : null}
              {hasMcq ? (
                <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
                  <BusQuizCta
                    persistenceRevision={quizProgressLocaleForHref(busQuizHref ?? quizIdToUrl(quizId)) === locale ? quizData.meta.persistenceRevision : undefined}
                    locale={locale}
                    quizId={quizId}
                    href={(busQuizHref ?? quizIdToUrl(quizId)) as Route}
                    prefetch={false}
                    className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100"
                  />
                </div>
              ) : null}
            </div>
          </div>
          {sections.length > 1 && (
            <div className="mb-4 flex gap-3">
              {sections.map((section) => (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setCurrentSection(section.key)}
                  className={`flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    currentSection === section.key
                      ? "bg-indigo-600 text-white shadow"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <span
          ref={visibilitySentinelRef}
          data-testid="quiz-visibility-sentinel"
          className="block h-px w-full"
          aria-hidden="true"
        />

        {currentSection === "mcq" && hasMcq && (
          <div className="mt-6 space-y-4">
            {mcqQuestions.map((question, index) => {
              const selected = mcqAnswers[question.id];

              return (
                <article
                  key={question.id}
                  className="rounded-xl bg-white p-6 shadow"
                >
                  <h3 className="mb-3 text-lg font-semibold text-gray-900">
                    {formatQuizRunner(copy.question, { number: index + 1 })}{" "}
                    {renderInlineMath(
                      question.question,
                      `mcq-question-${question.id}`
                    )}
                  </h3>
                  <div className="space-y-2">
                    {getQuizOptionOrder(quizId, question).map(
                      ({ option, originalIndex }) => {
                        const isSelected = selected === originalIndex;
                        const isCorrect = originalIndex === question.correct;

                        return (
                          <label
                            key={`${question.id}-${originalIndex}`}
                            className={`flex items-center gap-3 rounded-lg border-2 px-3 py-3 text-sm transition ${
                              isSelected
                                ? "border-indigo-500 bg-indigo-50"
                                : "border-gray-200 hover:border-indigo-300"
                            } ${
                              showMcqResults &&
                              (isCorrect
                                ? "border-green-500 bg-green-50"
                                : isSelected
                                ? "border-red-500 bg-red-50"
                                : "")
                            }`}
                          >
                            <input
                              type="radio"
                              name={`mcq-${question.id}`}
                              checked={isSelected}
                              onChange={() =>
                                handleMcqChange(question.id, originalIndex)
                              }
                              disabled={showMcqResults}
                              className="h-5 w-5"
                            />
                            <span className="flex-1 leading-relaxed text-gray-800">
                              {renderInlineMath(
                                option,
                                `mcq-option-${question.id}-${originalIndex}`
                              )}
                            </span>
                            {showMcqResults && isCorrect && (
                              <CheckCircle
                                className="text-green-600"
                                size={20}
                              />
                            )}
                            {showMcqResults && isSelected && !isCorrect && (
                              <XCircle className="text-red-600" size={20} />
                            )}
                          </label>
                        );
                      }
                    )}
                  </div>
                  {showMcqResults && (
                    <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                      <p className="font-semibold">{copy.explanation}</p>
                      <p className="mt-1 leading-relaxed">
                        {renderInlineMath(
                          question.explanation,
                          `mcq-explanation-${question.id}`
                        )}
                      </p>
                    </div>
                  )}
                </article>
              );
            })}

            <div className="sticky bottom-4 rounded-xl bg-white p-6 shadow">
              {!showMcqResults ? (
                <button
                  type="button"
                  onClick={checkMcqAnswers}
                  className="w-full rounded-lg bg-indigo-600 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
                >
                  {copy.check}
                </button>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-3 text-gray-900">
                    <Award className="text-yellow-500" size={32} />
                    <p className="text-xl font-semibold">
                      {formatQuizRunner(copy.score, {
                        score: calculateMcqScore(),
                        total: mcqQuestions.length,
                      })}
                    </p>
                  </div>
                  {calculateMcqScore() / mcqQuestions.length < 0.8 ? (
                    <QuizWhatsAppCta
                      locale={locale}
                      quizId={quizId}
                      quizTitle={displayTitle}
                      score={calculateMcqScore()}
                      total={mcqQuestions.length}
                    />
                  ) : (
                    <TelegramCTA
                      label={locale === "en" ? undefined : copy.telegram}
                      placement="quiz_results"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1b7eae] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a77a3]"
                    />
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={resetMcq}
                      className="flex-1 rounded-lg bg-gray-700 py-3 text-sm font-bold text-white transition hover:bg-gray-800"
                    >
                      {copy.restart}
                    </button>
                    <ShareQuizResult
                      locale={locale}
                      quizTitle={displayTitle}
                      score={calculateMcqScore()}
                      total={mcqQuestions.length}
                      className="flex-1 rounded-lg border border-gray-300 bg-white py-3 text-sm font-bold text-gray-700 transition hover:bg-gray-50 inline-flex items-center justify-center gap-2"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {currentSection === "open" && hasOpen && (
          <div className="mt-6 space-y-4">
            {openEndedQuestions.map((question, index) => (
              <StepReveal
                locale={locale}
                key={question.id}
                question={question}
                index={index}
                onInteract={markStarted}
              />
            ))}
          </div>
        )}

        {!hasMcq && !hasOpen && (
          <div className="mt-6 rounded-xl bg-white p-6 text-sm text-gray-700 shadow">
            {copy.soon}
          </div>
        )}
      </div>
    </div>
  );
}
